// The countries an organization can be in, as ISO 3166-1 alpha-2 codes: the one list behind the
// sign-up country, the organization settings and the onboarding account step. It is the supported
// set the rest of the app already keys on (SUPPORTED_COUNTRIES, RegionalSettingsContext, VAT
// formats, banks), so a country picked at sign-up is one every later screen can show.
//
// Names are never stored here — they come from Intl.DisplayNames in the active language, so the
// eleven locales need no country translations.
import { SUPPORTED_COUNTRIES } from "../types/onboarding";

export const ORGANIZATION_COUNTRY_CODES: readonly string[] = SUPPORTED_COUNTRIES.map((c) => c.code);

const ORGANIZATION_COUNTRY_SET = new Set(ORGANIZATION_COUNTRY_CODES);

const ISO_SHAPE = /^[A-Z]{2}$/;

/**
 * Trimmed, upper-cased ISO alpha-2 code, or "" when `value` is blank or not shaped like one.
 * "" means "no country" — never read it as a default country.
 */
export const normalizeIsoCountry = (value?: string | null): string => {
  const code = value?.trim().toUpperCase() ?? "";
  return ISO_SHAPE.test(code) ? code : "";
};

/** True for a code in ORGANIZATION_COUNTRY_CODES (case-insensitive). */
export const isOrganizationCountry = (value?: string | null): boolean =>
  ORGANIZATION_COUNTRY_SET.has(normalizeIsoCountry(value));

export interface CountryOption {
  /** ISO 3166-1 alpha-2, upper case — the value sent to the API. */
  code: string;
  /** Name in the requested language, or the code when the runtime has none. */
  name: string;
}

const regionNames = (language?: string): Intl.DisplayNames | null => {
  try {
    return new Intl.DisplayNames([language || "en"], { type: "region" });
  } catch {
    return null;
  }
};

/** The country's name in `language`; the code itself when Intl.DisplayNames has no name. */
export const countryName = (code: string, language?: string): string =>
  regionNames(language)?.of(code) ?? code;

const optionsByLanguage = new Map<string, readonly CountryOption[]>();

/** The organization countries, named and sorted in `language`. Cached per language. */
export const getCountryOptions = (language?: string): readonly CountryOption[] => {
  const key = language || "en";
  const cached = optionsByLanguage.get(key);
  if (cached) {
    return cached;
  }

  const names = regionNames(key);
  // The runtime's region names validated the tag; without them, sort in English rather than throw.
  const sortLocale = names ? key : "en";
  const options = ORGANIZATION_COUNTRY_CODES.map((code) => ({ code, name: names?.of(code) ?? code }));
  options.sort((a, b) => a.name.localeCompare(b.name, sortLocale));
  optionsByLanguage.set(key, options);
  return options;
};
